"use client";

// CSS-only stagger reveal — reemplazo de GSAP (2026-09-11 audit: todo era
// fade-up y+opacity con ScrollTrigger start "top 88%", 0 bytes JS).
// Misma API (stagger/y/duration) para no romper blog/page.tsx.

import { useEffect, useRef, type ReactNode } from "react";

export function StaggerReveal({
  children,
  className,
  stagger = 0.08,
  y = 16,
  duration = 0.6,
}: {
  children: React.ReactNode;
  className?: string;
  stagger?: number;
  y?: number;
  duration?: number;
}) {
  const ref = useRef<HTMLDivElement>(null);
  // y/duration se mantienen en la API por compat con blog/page.tsx; la
  // distancia/duración canónica vive en globals.css (.reveal/.reveal-in).
  void y;
  void duration;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    // 2026-09-11 P1: .reveal oculta (opacity 0) — solo se añade si el
    // IntersectionObserver existe Y se construye bien. Sin IO (SSR/jsdom,
    // navegadores viejos, constructor roto) el contenido queda visible.
    if (typeof IntersectionObserver === "undefined") return;
    const items = Array.from(el.children) as HTMLElement[];
    let io: IntersectionObserver;
    let revealedCount = 0;
    const revealItem = (item: HTMLElement) => {
      if (item.classList.contains("reveal-in")) return;
      item.classList.add("reveal-in");
      revealedCount += 1;
    };
    const revealAll = () => items.forEach(revealItem);
    try {
      io = new IntersectionObserver(
        (entries) => {
          for (const entry of entries) {
            if (!entry.isIntersecting) continue;
            const target = entry.target as HTMLElement | null;
            // 2026-10-12 FIX móvil blog/guides: el contenedor en 1 columna
            // es mucho más alto que el viewport (14 cards × ~460px ≈ 6400px
            // vs 727px de alto) y el threshold 0.12 exigía ~780px visibles —
            // condición imposible que dejaba todo en opacity 0. Doble red:
            // (1) el contenedor dispara con threshold 0 (1px visible basta,
            // revela todo como en desktop); (2) cada hijo se observa aparte
            // para saltos directos (anclas/teclado) que nunca intersecten el
            // contenedor. Sin target (mocks) = contenedor.
            if (!target || target === el) {
              revealAll();
              io.disconnect();
              return;
            }
            revealItem(target);
            if (revealedCount >= items.length) {
              io.disconnect();
              return;
            }
          }
        },
        { threshold: 0, rootMargin: "0px 0px -8% 0px" },
      );
    } catch {
      return;
    }
    items.forEach((item, i) => {
      item.classList.add("reveal");
      item.style.setProperty(
        "--reveal-delay",
        `${Math.round(i * stagger * 1000)}ms`,
      );
    });
    io.observe(el);
    items.forEach((item) => io.observe(item));
    return () => io.disconnect();
  }, [stagger, y, duration]);

  return (
    <div ref={ref} className={className}>
      {children as ReactNode}
    </div>
  );
}
